import React from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';

import type { MapProvider, MapProviderProps } from '../../model/map-provider.interface';

import { calculateMapCenter, MAP_DEFAULTS } from './lib/map-calculations';
import { LocationLayer } from './ui/location-layer';
import { CorridorLayer } from './ui/corridor-layer';
import { MapStateManager } from './map-state-manager';

import 'leaflet/dist/leaflet.css';
import './map-styles.css';

const MapContent = React.memo(function MapContent({
  locations,
  selectedLocation,
  corridors = [],
  config,
  onLocationSelect,
  dataLayer = 'stock',
  currentWeek = 0
}: MapProviderProps) {
  const showCorridors = config?.showCorridors ?? true;

  return (
    <>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      <MapStateManager locations={locations} initialFit={true} />

      <CorridorLayer corridors={corridors} showCorridors={showCorridors} />

      {locations.map((location) => (
        <LocationLayer
          key={location.id}
          location={location}
          selectedLocation={selectedLocation}
          dataLayer={dataLayer}
          currentWeek={currentWeek}
          onLocationSelect={onLocationSelect}
        />
      ))}
    </>
  );
});

export class OpenStreetMapProvider implements MapProvider {
  private mapKey = 'osm-map-instance';

  render(props: MapProviderProps): React.ReactElement {
    const { locations, config, style, className } = props;

    const center = calculateMapCenter(locations, config?.center);
    const zoom = config?.zoom ?? MAP_DEFAULTS.ZOOM;

    return (
      <MapContainer
        key={this.mapKey}
        center={center}
        zoom={zoom}
        style={style || { height: '100%', width: '100%' }}
        className={className || 'rounded-lg'}
        scrollWheelZoom={true}
        zoomControl={true}>
        <MapContent {...props} />
      </MapContainer>
    );
  }

  destroy(): void {
    // Cleanup if needed
  }
}
